import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { useLanguage } from '../contexts/LanguageContext';
import { getTierMeta } from '../utils/tierMeta';

const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001';
  }
  if (window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('10.')) {
    return `http://${window.location.hostname}:3001`;
  }
  return '';
};

const API_BASE = getBackendUrl();
const TABS = [
  { id: 'events', label: '📊 Live Events' },
  { id: 'bot-decisions', label: '🃏 Bot Card Decisions' },
  { id: 'trump-decisions', label: '🎯 Trump Decisions' },
  { id: 'rooms', label: '🏠 Rooms Management' },
  { id: 'users', label: '👥 Users' },
  { id: 'analytics', label: '📈 Analytics' },
  { id: 'messages', label: '✉️ Contact Messages' }
];
const SUIT_SYMBOLS = { S: '♠', H: '♥', D: '♦', C: '♣' };
const suitColor = (suit) => (suit === 'H' || suit === 'D' ? '#ff6b6b' : '#ffffff');
const formatThinkTime = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(2)}s`;
  }
  if (value >= 100) {
    return `${value.toFixed(0)}ms`;
  }
  if (value >= 10) {
    return `${value.toFixed(1)}ms`;
  }
  return `${value.toFixed(2)}ms`;
};
const EASTER_EGG_DETAILS = {
  minesweeper: {
    title: '🧨 Minesweeper Footer Link',
    description: 'Version footer portal into the retro Minesweeper mini-game.'
  },
  shadowruler: {
    title: '👑 Shadow Ruler Crown',
    description: 'Secret crown emoji that opens the Shadow Ruler wargame.'
  }
};
const formatStatNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString() : '0';
};

const escapeText = (value) => {
  const str = value === undefined || value === null ? '' : String(value);
  const replacements = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return str.replace(/[&<>"']/g, (char) => replacements[char] || char);
};

const formatHighScores = (scores) => {
  const result = [];
  const jewelsScore = scores.find((s) => s.game === 'jewels');
  if (jewelsScore) {
    result.push(`jewels: ${jewelsScore.score}`);
  }

  const difficulties = ['beginner', 'intermediate', 'expert'];
  const minesweeperScores = difficulties.map((diff) => {
    const entry = scores.find((s) => s.game === 'minesweeper' && s.difficulty === diff);
    return entry ? `${entry.score}s` : 'N/A';
  });
  if (minesweeperScores.some((item) => item !== 'N/A')) {
    result.push(`minesweeper: ${minesweeperScores.join(' - ')}`);
  }

  return result.join(' • ');
};

const AdminScreen = () => {
  const { t } = useLanguage();
  const [password, setPassword] = useState(() => sessionStorage.getItem('adminPassword') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('events');
  const [stats, setStats] = useState({ onlineUsers: 0, playersInGames: 0, gamesPlaying: 0, openLobbies: 0, unauthenticatedConnections: 0 });
  const [systemStats, setSystemStats] = useState(null);
  const [version, setVersion] = useState('');
  const [events, setEvents] = useState([]);
  const [botDecisions, setBotDecisions] = useState([]);
  const [trumpDecisions, setTrumpDecisions] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [analyticsRange, setAnalyticsRange] = useState('day');
  const [analyticsData, setAnalyticsData] = useState({ summary: null, insights: null, events: [] });
  const [easterEggStats, setEasterEggStats] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const socketRef = useRef(null);

  const fetchAuthedJson = useCallback(async (path, options = {}) => {
    if (!password) {
      throw new Error('Missing admin password');
    }
    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
    const response = await fetch(url, {
      ...options,
      headers: { ...options.headers, 'x-admin-password': password }
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Request failed');
    }
    if (response.status === 204) {
      return null;
    }
    return response.json();
  }, [password]);

  const fetchVersion = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/version`);
      if (response.ok) {
        const data = await response.json();
        setVersion(data.version || '');
      }
    } catch (error) {
      console.error('Failed to load version:', error);
    }
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const data = await fetchAuthedJson('/api/admin/rooms');
      const list = data?.rooms || data || [];
      setRooms(list);
    } catch (error) {
      console.error('Failed to load rooms', error);
    }
  }, [fetchAuthedJson]);

  const fetchBotDecisions = useCallback(async () => {
    try {
      const data = await fetchAuthedJson('/api/admin/bot-ai-logs?limit=30');
      const logs = Array.isArray(data?.logs) ? data.logs : [];
      setBotDecisions(logs.filter((log) => log?.chosenCard));
    } catch (error) {
      console.error('Failed to load bot decisions', error);
    }
  }, [fetchAuthedJson]);

  const fetchTrumpDecisions = useCallback(async () => {
    try {
      const data = await fetchAuthedJson('/api/admin/bot-ai-logs?limit=30');
      const logs = Array.isArray(data?.logs) ? data.logs : [];
      setTrumpDecisions(logs.filter((log) => log?.trumpDecision !== undefined));
    } catch (error) {
      console.error('Failed to load trump decisions', error);
    }
  }, [fetchAuthedJson]);

  const fetchContactMessages = useCallback(async () => {
    try {
      setContactError(null);
      setContactLoading(true);
      const data = await fetchAuthedJson('/api/admin/messages');
      setContactMessages(Array.isArray(data?.messages) ? data.messages : []);
    } catch (error) {
      console.error('Failed to load contact messages', error);
      setContactError(error.message);
    } finally {
      setContactLoading(false);
    }
  }, [fetchAuthedJson]);

  const fetchUsersData = useCallback(async () => {
    try {
      const [statsResponse, usersResponse] = await Promise.all([
        fetchAuthedJson('/api/admin/users/stats'),
        fetchAuthedJson('/api/admin/users')
      ]);
      setUserStats(statsResponse || {});
      setUsers(usersResponse?.users || []);
    } catch (error) {
      console.error('Failed to load users', error);
    }
  }, [fetchAuthedJson]);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      const summary = await fetchAuthedJson('/api/admin/analytics/summary/stats');
      setAnalyticsData((prev) => ({
        summary: summary || null,
        insights: prev?.insights || null,
        events: prev?.events || []
      }));
    } catch (error) {
      console.error('Failed to load analytics', error);
    }
  }, [fetchAuthedJson]);

  const fetchEasterEggStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/easter-eggs/stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch easter egg stats');
      }
      const data = await response.json();
      const statsPayload = Array.isArray(data?.stats) ? data.stats : data || [];
      setEasterEggStats(statsPayload);
    } catch (error) {
      console.error('Failed to load easter egg stats', error);
    }
  }, []);

  const handleLogin = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!password) {
      alert('Enter admin password');
      return;
    }
    try {
      setIsLoading(true);
      await fetchAuthedJson('/api/admin/rooms');
      sessionStorage.setItem('adminPassword', password);
      setIsAuthenticated(true);
    } catch (error) {
      if (e) alert('Invalid password or unable to reach server');
      console.error('Admin login failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchAuthedJson, password]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    setRooms([]);
    setBotDecisions([]);
    setTrumpDecisions([]);
    setUsers([]);
    setEvents([]);
    setAnalyticsData({ summary: null, insights: null, events: [] });
    setUserStats({});
    setEasterEggStats([]);
    setStats({ onlineUsers: 0, playersInGames: 0, gamesPlaying: 0, openLobbies: 0, unauthenticatedConnections: 0 });
    setSystemStats(null);
    setVersion('');
    setPassword('');
    sessionStorage.removeItem('adminPassword');
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  };

  const initializeDashboard = useCallback(async () => {
    try {
      await Promise.all([
        fetchVersion(),
        fetchRooms(),
        fetchBotDecisions(),
        fetchTrumpDecisions(),
        fetchUsersData(),
        fetchAnalyticsData(),
        fetchEasterEggStats()
      ]);
    } catch (error) {
      console.error('Failed to initialize admin dashboard', error);
    }
  }, [fetchAnalyticsData, fetchBotDecisions, fetchEasterEggStats, fetchRooms, fetchTrumpDecisions, fetchUsersData, fetchVersion]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    initializeDashboard();
  }, [initializeDashboard, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated || !password) {
      return;
    }
    handleLogin();
  }, [password, isAuthenticated, handleLogin]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    fetchAnalyticsData();
  }, [fetchAnalyticsData, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (activeTab === 'bot-decisions') {
      fetchBotDecisions();
    } else if (activeTab === 'trump-decisions') {
      fetchTrumpDecisions();
    } else if (activeTab === 'rooms') {
      fetchRooms();
    } else if (activeTab === 'users') {
      fetchUsersData();
    } else if (activeTab === 'analytics') {
      fetchAnalyticsData();
      fetchEasterEggStats();
    } else if (activeTab === 'messages') {
      fetchContactMessages();
    }
  }, [activeTab, fetchAnalyticsData, fetchBotDecisions, fetchContactMessages, fetchEasterEggStats, fetchRooms, fetchTrumpDecisions, fetchUsersData, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const socket = io(`${API_BASE}/admin`, { transports: ['websocket'], auth: { password } });
    socketRef.current = socket;
    socket.on('connect', () => {
      socket.emit('get-stats');
    });
    socket.on('stats-update', (incoming) => {
      setStats({
        onlineUsers: incoming?.onlineUsers || 0,
        playersInGames: incoming?.playersInGames || 0,
        gamesPlaying: incoming?.gamesPlaying || 0,
        openLobbies: incoming?.openLobbies || 0,
        unauthenticatedConnections: incoming?.unauthenticatedConnections || 0
      });
      if (incoming?.system) {
        setSystemStats(incoming.system);
      }
    });
    const handleBotDecisionPush = (entry) => {
      setBotDecisions((prev) => {
        const next = [entry, ...prev];
        return next.slice(0, 30);
      });
    };

    const handleTrumpDecisionPush = (entry) => {
      setTrumpDecisions((prev) => {
        const next = [entry, ...prev];
        return next.slice(0, 30);
      });
    };

    socket.on('log-event', (entry) => {
      setEvents((prev) => {
        const next = [...prev, entry];
        if (next.length > 100) {
          next.shift();
        }
        return next;
      });
    });
    socket.on('bot-decision-log', handleBotDecisionPush);
    socket.on('trump-decision-log', handleTrumpDecisionPush);
    return () => {
      socket.off('bot-decision-log', handleBotDecisionPush);
      socket.off('trump-decision-log', handleTrumpDecisionPush);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isAuthenticated, password]);

  const handleDeleteRoom = async (roomId) => {
    if (!roomId) {
      return;
    }
    if (!window.confirm(`Delete room ${roomId}?`)) {
      return;
    }
    try {
      await fetchAuthedJson(`/api/admin/rooms/${roomId}`, { method: 'DELETE' });
      fetchRooms();
    } catch (error) {
      alert('Failed to delete room');
      console.error('Delete room failed:', error);
    }
  };

  const handleClearRooms = async () => {
    if (!window.confirm('Delete ALL rooms? This cannot be undone.')) {
      return;
    }
    try {
      await fetchAuthedJson('/api/admin/rooms', { method: 'DELETE' });
      fetchRooms();
    } catch (error) {
      alert('Failed to clear rooms');
      console.error('Clear rooms failed:', error);
    }
  };

  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) {
      fetchUsersData();
      return;
    }
    try {
      const data = await fetchAuthedJson(`/api/admin/users/search?query=${encodeURIComponent(searchQuery.trim())}`);
      setUsers(data?.users || []);
    } catch (error) {
      alert('Search failed');
      console.error('User search failed:', error);
    }
  };

  const handleBanUser = async (userId) => {
    const reason = window.prompt('Reason for ban?', 'Violation');
    if (reason === null) {
      return;
    }
    try {
      await fetchAuthedJson(`/api/admin/users/${userId}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      fetchUsersData();
    } catch (error) {
      alert('Failed to ban user');
      console.error('Ban user failed:', error);
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await fetchAuthedJson(`/api/admin/users/${userId}/unban`, { method: 'POST' });
      fetchUsersData();
    } catch (error) {
      alert('Failed to unban user');
      console.error('Unban user failed:', error);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const data = await fetchAuthedJson(`/api/admin/users/${userId}/reset-password`, { method: 'POST' });
      alert(`Temporary password for ${data?.username || 'user'}: ${data?.temporaryPassword}`);
      fetchUsersData();
    } catch (error) {
      alert('Failed to reset password');
      console.error('Reset password failed:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user permanently?')) {
      return;
    }
    try {
      await fetchAuthedJson(`/api/admin/users/${userId}`, { method: 'DELETE' });
      fetchUsersData();
    } catch (error) {
      alert('Failed to delete user');
      console.error('Delete user failed:', error);
    }
  };

  const renderCardList = (hand = []) => {
    if (!hand || hand.length === 0) {
      return <span style={{ color: '#666' }}>-</span>;
    }
    return (
      <span>
        {hand.map((rawCard, index) => {
          const parsed = typeof rawCard === 'string'
            ? { rank: rawCard.slice(0, -1), suit: rawCard.slice(-1) }
            : rawCard;
          const rank = parsed?.rank || '';
          const suit = parsed?.suit;
          return (
            <React.Fragment key={`${rank || rawCard}-${index}`}>
              {index > 0 && ' '}
              {rank || suit ? (
                <span style={{ color: suitColor(suit), fontWeight: 'bold' }}>
                  {rank}{SUIT_SYMBOLS[suit] || ''}
                </span>
              ) : (
                <span style={{ color: '#666' }}>-</span>
              )}
            </React.Fragment>
          );
        })}
      </span>
    );
  };

  const renderTierBadge = (tier = 0) => {
    const meta = getTierMeta(tier);
    const label = t(meta.labelKey) || meta.defaultLabel;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: 6,
          padding: '2px 6px',
          borderRadius: 999,
          background: meta.color,
          color: meta.level === 0 ? '#fff' : meta.text,
          fontSize: 12,
          fontWeight: 700,
          border: '1px solid rgba(0,0,0,0.15)'
        }}
        title={`Tier ${meta.level}: ${label}`}
      >
        {meta.icon}
      </span>
    );
  };

  const renderEventsTab = () => (
    <div style={tabContentStyle}>
      {events.length === 0 ? (
        <div style={logEntryStyle}>Waiting for live events...</div>
      ) : (
        events.map((event, index) => (
          <div key={index} style={logEntryStyle}>
            <span style={{ color: '#999' }}>{new Date(event?.timestamp || Date.now()).toLocaleTimeString()}</span>
            <span style={{ marginLeft: 8, color: '#667eea', fontWeight: 'bold' }}>{event?.event || 'Event'}</span>
            <div style={{ marginTop: 4, fontSize: 13 }}>{event?.details || 'No details'}</div>
          </div>
        ))
      )}
    </div>
  );

  const renderBotDecisionsTab = () => (
    <div style={tabContentStyle}>
      <div style={tabToolbarStyle}>
        <button style={refreshButtonStyle} onClick={fetchBotDecisions}>🔄 Refresh</button>
      </div>
      {botDecisions.length === 0 ? (
        <div style={logEntryStyle}>No bot card decisions recorded yet.</div>
      ) : (
        botDecisions.map((log, index) => (
          <div key={index} style={botDecisionStyle}>
            <div>
              <strong>🤖 {log.botName || 'Bot'}</strong> played {renderCardList([log.chosenCard])}
              {log.thinkTimeMs !== null && log.thinkTimeMs !== undefined && (
                <span style={{ marginLeft: 8, color: '#26c6da', fontWeight: 600 }}>
                  ⏱ {formatThinkTime(log.thinkTimeMs)}
                </span>
              )}
              <span style={{ marginLeft: 8 }}>| Hand: {renderCardList(log.botHand || [])}</span>
            </div>
            <div style={{ marginTop: 6 }}>
              Board: {renderCardList((log.trickCards && log.trickCards.length ? log.trickCards : new Array(4).fill(null)))}
            </div>
            <div style={{ marginTop: 6 }}>
              Trump: {log.trumpCard || log.trump ? renderCardList([log.trumpCard || log.trump]) : 'None'}
            </div>
            <div style={botMetaStyle}>
              {new Date(log.timestamp).toLocaleTimeString()} | {log.decisionReason || 'No reason provided'}
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderTrumpDecisionsTab = () => (
    <div style={tabContentStyle}>
      <div style={tabToolbarStyle}>
        <button style={refreshButtonStyle} onClick={fetchTrumpDecisions}>🔄 Refresh</button>
      </div>
      {trumpDecisions.length === 0 ? (
        <div style={logEntryStyle}>No trump decisions recorded yet.</div>
      ) : (
        trumpDecisions.map((log, index) => (
          <div key={index} style={botDecisionStyle}>
            <div>
              <strong>🤖 {log.botName || 'Bot'}</strong> {log.trumpDecision ? '✅ ACCEPTED' : '❌ REJECTED'}
              &nbsp;Trump <span style={{ color: suitColor(log.trump) }}>{SUIT_SYMBOLS[log.trump] || log.trump || 'None'}</span>
            </div>
            <div style={{ marginTop: 6 }}>Hand: {renderCardList(log.botHand || [])}</div>
            <div style={botMetaStyle}>
              {new Date(log.timestamp).toLocaleTimeString()} | Trump Strength: {log.trumpStrength || 0} | Hand Strength: {log.handStrength || 0}
            </div>
            {log.decisionReason && (
              <div style={{ marginTop: 4, fontStyle: 'italic', color: '#ccc' }}>{log.decisionReason}</div>
            )}
            {log.context?.rlFailure && (
              <div style={{ marginTop: 6, color: '#ffb74d', fontWeight: 600 }}>
                ⚠️ RL trump logic unavailable: {log.context.rlFailureReason || 'Wrapper provided no data'}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderRoomsTab = () => (
    <div style={tabContentStyle}>
      <div style={{ ...tabToolbarStyle, justifyContent: 'space-between' }}>
        <div>
          <button style={refreshButtonStyle} onClick={fetchRooms}>🔄 Refresh Rooms</button>
          <button style={{ ...refreshButtonStyle, background: '#f44336', marginLeft: 8 }} onClick={handleClearRooms}>🗑️ Clear All Rooms</button>
        </div>
        <div style={{ color: '#aaa' }}>Total: {rooms.length}</div>
      </div>
      {rooms.length === 0 ? (
        <div style={logEntryStyle}>No rooms found.</div>
      ) : (
        rooms.map((room) => (
          <div key={room.roomId || room.id} style={roomCardStyle}>
            <div>
              <strong>{room.roomId || room.id || 'Room'}</strong> &nbsp;|&nbsp; {room.status || 'unknown'} &nbsp;|&nbsp; {room.playerCount || (room.players?.length || 0)}/4
            </div>
            <div style={{ fontSize: 13, color: '#ccc', marginTop: 4 }}>
              Host: {room.hostId || '—'} | Phase: {room.gamePhase || 'unknown'} | Trick #{room.currentTrick || 0}
            </div>
            <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>
              Players: {(room.players || [])
                .map((p) => (p?.username ? `${p.username}${p.isBot ? '🤖' : ''}` : null))
                .filter(Boolean)
                .join(', ') || 'None'}
            </div>
            <button style={deleteButtonStyle} onClick={() => handleDeleteRoom(room.roomId || room.id)}>Delete</button>
          </div>
        ))
      )}
    </div>
  );

  const renderUsersTab = () => (
    <div style={tabContentStyle}>
      <div style={{ ...tabToolbarStyle, flexWrap: 'wrap' }}>
        <button style={refreshButtonStyle} onClick={fetchUsersData}>🔄 Refresh Users</button>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users..."
            style={searchInputStyle}
          />
          <button style={refreshButtonStyle} onClick={handleSearchUsers}>🔍 Search</button>
          <button style={refreshButtonStyle} onClick={() => { setSearchQuery(''); fetchUsersData(); }}>Reset</button>
        </div>
      </div>
      <div style={userStatsGridStyle}>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{userStats.totalUsers ?? '-'}</div>
          <div>Registered Users</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{userStats.activeUsers ?? '-'}</div>
          <div>Active Users</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{userStats.bannedUsers ?? '-'}</div>
          <div>Banned</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{userStats.activeToday ?? '-'}</div>
          <div>Active Today (registered)</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{userStats.guestUsers ?? '-'}</div>
          <div>Guests (last 3 days)</div>
        </div>
      </div>
      {users.length === 0 ? (
        <div style={logEntryStyle}>No users found.</div>
      ) : (
        users.map((user) => {
          const statusClass = user.isBanned ? '#f44336' : (user.mustChangePassword ? '#ffa726' : '#4CAF50');
          return (
            <div key={user._id} style={userCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <span>{user.username}</span>
                    {!user.isGuest && renderTierBadge(user.challengeTier || 0)}
                  </div>
                  <div style={{ color: statusClass, fontWeight: 'bold', fontSize: 13 }}>
                    {user.isBanned ? 'Banned' : user.mustChangePassword ? 'Password Reset' : 'Active'}
                  </div>
                  <div style={userMetaStyle}>Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}</div>
                  {!user.isGuest && (
                    <div style={userMetaStyle}>
                      Challenge Tier: {(() => {
                        const tierMeta = getTierMeta(user.challengeTier || 0);
                        return t(tierMeta.labelKey) || tierMeta.defaultLabel;
                      })()}
                    </div>
                  )}
                  <div style={userMetaStyle}>Last Login: {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}</div>
                  <div style={userMetaStyle}>IP: {user.isGuest ? (user.lastKnownIpHash || '—') : (user.lastKnownIp || '—')}</div>
                  {Array.isArray(user.highScores) && user.highScores.length > 0 && (
                    <div style={userMetaStyle}>{formatHighScores(user.highScores)}</div>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {user.isBanned ? (
                    <button style={actionButtonStyle('#4CAF50')} onClick={() => handleUnbanUser(user._id)}>Unban</button>
                  ) : (
                    <button style={actionButtonStyle('#f44336')} onClick={() => handleBanUser(user._id)}>Ban</button>
                  )}
                  <button style={actionButtonStyle('#2196F3')} onClick={() => handleResetPassword(user._id)}>Reset PW</button>
                  <button style={actionButtonStyle('#9e9e9e')} onClick={() => handleDeleteUser(user._id)}>Delete</button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const renderAnalyticsTab = () => {
    const eggs = Array.isArray(easterEggStats) ? easterEggStats.filter((stat) => stat && stat.eggKey) : [];
    const maxUnique = Math.max(1, ...eggs.map((stat) => Number(stat.uniqueIpCount) || 0));
    const maxClicks = Math.max(1, ...eggs.map((stat) => Number(stat.clickCount) || 0));

    const renderEggCard = (stat) => {
      const totals = {
        unique: Number(stat.uniqueIpCount) || 0,
        clicks: Number(stat.clickCount) || 0
      };
      const friendly = EASTER_EGG_DETAILS[stat.eggKey] || {
        title: stat.eggKey,
        description: 'Custom easter egg entry'
      };
      const uniquePct = Math.min(100, Math.max(4, (totals.unique / maxUnique) * 100));
      const clickPct = Math.min(100, Math.max(4, (totals.clicks / maxClicks) * 100));
      return (
        <div key={stat.eggKey} style={easterEggCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{friendly.title}</div>
            <span style={eggKeyPillStyle}>{stat.eggKey}</span>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: 12 }}>{friendly.description}</div>
          <div style={eggMetricRowStyle}>
            <div>
              <div style={eggMetricLabelStyle}>Unique Seekers</div>
              <div style={eggMetricValueStyle}>{formatStatNumber(totals.unique)}</div>
            </div>
            <div>
              <div style={eggMetricLabelStyle}>Total Clicks</div>
              <div style={eggMetricValueStyle}>{formatStatNumber(totals.clicks)}</div>
            </div>
          </div>
          <div>
            <div style={eggMetricLabelStyle}>Discovery Progress</div>
            <div style={progressTrackStyle}>
              <div style={progressFillStyle('#58d68d', uniquePct)} />
            </div>
            <div style={{ ...eggMetricLabelStyle, marginTop: 6 }}>Click Momentum</div>
            <div style={progressTrackStyle}>
              <div style={progressFillStyle('#f9ca24', clickPct)} />
            </div>
          </div>
          <div style={eggMetaStyle}>
            {stat.updatedAt ? `Updated ${new Date(stat.updatedAt).toLocaleString()}` : 'Awaiting first click'}
          </div>
        </div>
      );
    };

    return (
      <div style={tabContentStyle}>
        <div style={{ ...tabToolbarStyle, flexWrap: 'wrap', gap: 8 }}>
          <div>
            {['day', 'week', 'month'].map((range) => (
              <button
                key={range}
                style={{
                  ...refreshButtonStyle,
                  background: analyticsRange === range ? '#667eea' : '#2a2a4a'
                }}
                onClick={() => setAnalyticsRange(range)}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>
          <button style={refreshButtonStyle} onClick={() => {
            fetchAnalyticsData();
            fetchEasterEggStats();
          }}>🔄 Refresh</button>
        </div>
        <div style={analyticsSummaryGridStyle}>
          <div style={statCardStyle}>
            <div style={statNumberStyle}>{analyticsData.summary?.totalGames ?? '-'}</div>
            <div>Total Games</div>
          </div>
          <div style={statCardStyle}>
            <div style={statNumberStyle}>{analyticsData.summary?.completionRate ?? '-'}%</div>
            <div>Completion Rate</div>
          </div>
          <div style={statCardStyle}>
            <div style={statNumberStyle}>{analyticsData.summary?.avgDurationMinutes ?? '-'}</div>
            <div>Avg Duration (min)</div>
          </div>
          <div style={statCardStyle}>
            <div style={statNumberStyle}>{analyticsData.summary?.peakPlayers ?? '-'}</div>
            <div>Peak Players</div>
          </div>
        </div>
        <div style={analyticsCardStyle}>
          <h3 style={{ marginBottom: 10 }}>Easter Egg Discovery</h3>
          {eggs.length === 0 ? (
            <div>No clicks recorded yet. Encourage players to find the secrets!</div>
          ) : (
            <div style={easterEggGridStyle}>
              {eggs.map(renderEggCard)}
            </div>
          )}
        </div>
        {analyticsData.insights && (
          <div style={analyticsCardStyle}>
            <h3 style={{ marginBottom: 10 }}>Insights (Last Week)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              <div>
                <h4>Most Active Hours</h4>
                {(analyticsData.insights.mostActiveHours || []).map((item) => (
                  <div key={`${item.hour}-hour`}>{item.hour}:00 — {item.count} events</div>
                ))}
              </div>
              <div>
                <h4>Most Active Days</h4>
                {(analyticsData.insights.mostActiveDays || []).map((item) => (
                  <div key={`${item.day}-day`}>{item.day} — {item.count} events</div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <div>Game Completion Rate: {analyticsData.insights.gameCompletionRate ?? 0}%</div>
              <div>Total Events Last Week: {analyticsData.insights.totalEventsLastWeek ?? 0}</div>
            </div>
          </div>
        )}
        <div style={analyticsCardStyle}>
          <h3 style={{ marginBottom: 10 }}>{analyticsRange.toUpperCase()} Events</h3>
          {analyticsData.events.length === 0 ? (
            <div>No analytics data available.</div>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {analyticsData.events.map((event, index) => (
                <div key={index} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <strong>{event.eventType}</strong> — {event.count} occurrences
                  <span style={{ float: 'right', color: '#999' }}>{new Date(event.date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMessagesTab = () => (
    <div style={tabContentStyle}>
      <div style={{ ...tabToolbarStyle, justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <button style={refreshButtonStyle} onClick={fetchContactMessages}>🔄 Refresh</button>
        <div style={{ color: '#aaa' }}>Total: {contactMessages.length}</div>
      </div>
      {contactError && (
        <div style={errorBannerStyle}>{contactError}</div>
      )}
      {contactLoading ? (
        <div style={logEntryStyle}>Loading contact messages…</div>
      ) : contactMessages.length === 0 ? (
        <div style={logEntryStyle}>No messages yet.</div>
      ) : (
        contactMessages.map((msg) => (
          <div key={msg._id} style={contactCardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <strong>{escapeText(msg.name || 'Anonymous')}</strong>
                <span style={{ marginLeft: 8, color: '#a0aec0' }}>{escapeText(msg.email || '')}</span>
              </div>
              <span style={{ color: '#94a3b8' }}>{new Date(msg.createdAt).toLocaleString()}</span>
            </div>
            <div style={contactMetaStyle}>
              User: {msg.user ? `${escapeText(msg.user.username)}${msg.user.isGuest ? ' (guest)' : ''}` : 'Not linked'}
              {msg.user?.challengeTier !== undefined && (
                <span> • Tier {msg.user.challengeTier}</span>
              )}
            </div>
            <div style={contactMetaStyle}>IP: {escapeText(msg.ipAddress || 'unknown')}</div>
            <p style={{ marginTop: 10, whiteSpace: 'pre-wrap', color: '#e2e8f0' }}>{escapeText(msg.message)}</p>
            {Array.isArray(msg.highScores) && msg.highScores.length > 0 && (
              <div style={contactMetaStyle}>{formatHighScores(msg.highScores)}</div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'bot-decisions':
        return renderBotDecisionsTab();
      case 'trump-decisions':
        return renderTrumpDecisionsTab();
      case 'rooms':
        return renderRoomsTab();
      case 'users':
        return renderUsersTab();
      case 'analytics':
        return renderAnalyticsTab();
      case 'messages':
        return renderMessagesTab();
      default:
        return renderEventsTab();
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={loginContainerStyle}>
        <form onSubmit={handleLogin} style={loginFormStyle}>
          <h1>Admin Access</h1>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter Admin Password"
            style={loginInputStyle}
          />
          <button type="submit" style={loginButtonStyle} disabled={isLoading}>
            {isLoading ? 'Checking...' : 'Login'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={dashboardStyle}>
      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>🎮 Kwajongen Admin Dashboard</h1>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)' }}>Version: {version || 'Loading...'}</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ color: '#a5ffb2', fontWeight: 'bold' }}>● Live Monitoring Active</div>
          <button style={refreshButtonStyle} onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div style={statsGridStyle}>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{stats.onlineUsers}</div>
          <div>People Online</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{stats.playersInGames}</div>
          <div>Playing Now</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{stats.gamesPlaying}</div>
          <div>Games In Progress</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{stats.openLobbies}</div>
          <div>Open Lobbies</div>
        </div>
        <div style={statCardStyle}>
          <div style={statNumberStyle}>{stats.unauthenticatedConnections}</div>
          <div>Not Logged In (connections)</div>
        </div>
        {systemStats && (
          <div style={statCardStyle}>
            <div style={{ fontSize: 14 }}>System: {systemStats.memory?.used || '-'} | Uptime: {systemStats.uptime || '-'}</div>
          </div>
        )}
      </div>

      <div style={tabsBarStyle}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...tabButtonStyle,
              background: activeTab === tab.id ? '#667eea' : 'transparent',
              color: activeTab === tab.id ? '#fff' : '#bbb'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {renderActiveTab()}
    </div>
  );
};

const dashboardStyle = {
  minHeight: '100vh',
  background: '#1a1a2e',
  color: '#fff',
  paddingBottom: 40
};

const headerStyle = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  padding: '20px 32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 16
};

const statsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 16,
  padding: '20px'
};

const statCardStyle = {
  background: '#16213e',
  padding: '20px',
  borderRadius: 10,
  borderLeft: '4px solid #667eea',
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
};

const statNumberStyle = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#667eea'
};

const tabsBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  margin: '0 20px'
};

const tabButtonStyle = {
  border: 'none',
  padding: '12px 18px',
  borderRadius: '10px 10px 0 0',
  cursor: 'pointer',
  fontWeight: 'bold'
};

const tabContentStyle = {
  background: '#16213e',
  margin: '0 20px 40px 20px',
  borderRadius: '0 0 10px 10px',
  minHeight: 400,
  padding: 20,
  border: '1px solid rgba(255,255,255,0.08)'
};

const tabToolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
  gap: 12
};

const refreshButtonStyle = {
  background: '#2a2a4a',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)',
  padding: '8px 14px',
  borderRadius: 6,
  cursor: 'pointer'
};

const logEntryStyle = {
  background: 'rgba(255,255,255,0.05)',
  borderLeft: '4px solid #667eea',
  padding: '12px',
  marginBottom: 10,
  borderRadius: 8
};

const contactCardStyle = {
  background: 'rgba(255,255,255,0.05)',
  padding: 16,
  borderRadius: 10,
  marginBottom: 12,
  border: '1px solid rgba(255,255,255,0.08)'
};

const contactMetaStyle = {
  color: '#a5b4fc',
  fontSize: '0.85rem',
  marginTop: 4
};

const errorBannerStyle = {
  background: 'rgba(239, 68, 68, 0.2)',
  color: '#fecaca',
  padding: '10px 14px',
  borderRadius: 8,
  marginBottom: 12
};

const botDecisionStyle = {
  background: '#2a2a2a',
  padding: '14px',
  borderRadius: 8,
  marginBottom: 12,
  fontFamily: 'monospace',
  fontSize: 16
};

const botMetaStyle = {
  fontSize: 13,
  color: '#999',
  marginTop: 6
};

const roomCardStyle = {
  background: 'rgba(102, 126, 234, 0.08)',
  border: '1px solid rgba(102, 126, 234, 0.4)',
  borderRadius: 8,
  padding: '12px 16px',
  marginBottom: 10,
  position: 'relative'
};

const deleteButtonStyle = {
  position: 'absolute',
  top: 12,
  right: 16,
  background: '#f44336',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '4px 8px',
  cursor: 'pointer'
};

const userStatsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  marginBottom: 16
};

const userCardStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  padding: 16,
  marginBottom: 12
};

const userMetaStyle = {
  color: '#aaa',
  fontSize: 12
};

const actionButtonStyle = (color) => ({
  background: color,
  color: '#fff',
  border: 'none',
  padding: '6px 10px',
  borderRadius: 4,
  cursor: 'pointer'
});

const searchInputStyle = {
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.2)',
  background: '#1f1f2f',
  color: '#fff'
};

const analyticsSummaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 12,
  marginBottom: 16
};

const analyticsCardStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  padding: 16,
  marginBottom: 12
};

const easterEggGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 16
};

const easterEggCardStyle = {
  background: 'rgba(22, 33, 62, 0.8)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 6
};

const eggMetricRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap'
};

const eggMetricLabelStyle = {
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: 11,
  color: 'rgba(255,255,255,0.6)'
};

const eggMetricValueStyle = {
  fontSize: '1.8rem',
  fontWeight: 'bold',
  color: '#fff'
};

const progressTrackStyle = {
  height: 8,
  background: 'rgba(255,255,255,0.08)',
  borderRadius: 999,
  overflow: 'hidden'
};

const progressFillStyle = (background, width) => ({
  height: '100%',
  width: `${Math.min(100, Math.max(0, width))}%`,
  background,
  borderRadius: 'inherit',
  transition: 'width 0.3s ease'
});

const eggMetaStyle = {
  color: 'rgba(255,255,255,0.5)',
  fontSize: 12,
  marginTop: 4
};

const eggKeyPillStyle = {
  fontSize: 12,
  padding: '2px 10px',
  borderRadius: 999,
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.15)'
};

const loginContainerStyle = {
  minHeight: '100vh',
  background: '#1a1a2e',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const loginFormStyle = {
  background: 'rgba(255,255,255,0.05)',
  padding: '30px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.2)',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  width: 'min(360px, 90vw)'
};

const loginInputStyle = {
  padding: '12px',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.3)',
  background: '#2a2a4a',
  color: '#fff'
};

const loginButtonStyle = {
  background: '#667eea',
  border: 'none',
  color: '#fff',
  padding: '12px',
  borderRadius: 6,
  cursor: 'pointer',
  fontWeight: 'bold'
};

export default AdminScreen;
